import React from "react";
import { ChildrenProps } from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { permissions, StudentNoteNoteType } from "../../../../Utilities";

const basePermissions: string[] = [];

export type StudentNoteModifyPermissionRequiredProps = {
	isAuthor: boolean;
	noteType: StudentNoteNoteType;
} & Partial<ChildrenProps>;

export function StudentNoteModifyPermissionRequired({
	isAuthor,
	noteType,
	children,
}: StudentNoteModifyPermissionRequiredProps) {
	const check = React.useCallback(
		(userPermissions: Record<string, boolean>) => {
			return (
				checkUpdatePermission(isAuthor, noteType, userPermissions) ||
				checkDeletePermission(isAuthor, noteType, userPermissions)
			);
		},
		[isAuthor, noteType],
	);

	return (
		<PermissionRequired requiredPermissions={basePermissions} customCheck={check} customCheckMethod="and">
			{children}
		</PermissionRequired>
	);
}

export type StudentNoteUpdatePermissionRequiredProps = {
	isAuthor: boolean;
	noteType: StudentNoteNoteType;
} & Partial<ChildrenProps>;

export function StudentNoteUpdatePermissionRequired({
	isAuthor,
	noteType,
	children,
}: StudentNoteUpdatePermissionRequiredProps) {
	const check = React.useCallback(
		(userPermissions: Record<string, boolean>) => {
			return checkUpdatePermission(isAuthor, noteType, userPermissions);
		},
		[isAuthor, noteType],
	);

	return (
		<PermissionRequired requiredPermissions={basePermissions} customCheck={check} customCheckMethod="and">
			{children}
		</PermissionRequired>
	);
}

function checkUpdatePermission(
	isAuthor: boolean,
	noteType: StudentNoteNoteType,
	userPermissions: Record<string, boolean>,
) {
	const hasBasePermission =
		userPermissions[permissions.StudentNote.Update.Admin] ||
		(isAuthor && userPermissions[permissions.StudentNote.Update.Basic]);
	if (!hasBasePermission) {
		return false;
	}

	let noteTypePermissions = permissions.StudentNote.Update;
	switch (noteType) {
		case StudentNoteNoteType.Academic:
			noteTypePermissions = permissions.StudentAcademicNote.Update;
			break;
		case StudentNoteNoteType.Attendance:
			noteTypePermissions = permissions.StudentAttendanceNote.Update;
			break;
		case StudentNoteNoteType.Behavior:
			noteTypePermissions = permissions.StudentBehaviorNote.Update;
			break;
		case StudentNoteNoteType.Health:
			noteTypePermissions = permissions.StudentHealthNote.Update;
			break;
		case StudentNoteNoteType.Incident:
			noteTypePermissions = permissions.StudentIncidentNote.Update;
			break;
		case StudentNoteNoteType.Injury:
			noteTypePermissions = permissions.StudentInjuryNote.Update;
			break;
		case StudentNoteNoteType.Medication:
			noteTypePermissions = permissions.StudentMedicationNote.Update;
			break;
		case StudentNoteNoteType.SocialEmotional:
			noteTypePermissions = permissions.StudentSocialEmotionalNote.Update;
			break;
	}

	return userPermissions[noteTypePermissions.Admin] || (isAuthor && userPermissions[noteTypePermissions.Basic]);
}

export type StudentNoteDeletePermissionRequiredProps = {
	isAuthor: boolean;
	noteType: StudentNoteNoteType;
} & Partial<ChildrenProps>;

export function StudentNoteDeletePermissionRequired({
	isAuthor,
	noteType,
	children,
}: StudentNoteDeletePermissionRequiredProps) {
	const check = React.useCallback(
		(userPermissions: Record<string, boolean>) => {
			return checkDeletePermission(isAuthor, noteType, userPermissions);
		},
		[isAuthor, noteType],
	);

	return (
		<PermissionRequired requiredPermissions={basePermissions} customCheck={check} customCheckMethod="and">
			{children}
		</PermissionRequired>
	);
}

function checkDeletePermission(
	isAuthor: boolean,
	noteType: StudentNoteNoteType,
	userPermissions: Record<string, boolean>,
) {
	const hasBasePermission =
		userPermissions[permissions.StudentNote.Delete.Admin] ||
		(isAuthor && userPermissions[permissions.StudentNote.Delete.Basic]);
	if (!hasBasePermission) {
		return false;
	}

	let noteTypePermissions = permissions.StudentNote.Delete;
	switch (noteType) {
		case StudentNoteNoteType.Academic:
			noteTypePermissions = permissions.StudentAcademicNote.Delete;
			break;
		case StudentNoteNoteType.Attendance:
			noteTypePermissions = permissions.StudentAttendanceNote.Delete;
			break;
		case StudentNoteNoteType.Behavior:
			noteTypePermissions = permissions.StudentBehaviorNote.Delete;
			break;
		case StudentNoteNoteType.Health:
			noteTypePermissions = permissions.StudentHealthNote.Delete;
			break;
		case StudentNoteNoteType.Incident:
			noteTypePermissions = permissions.StudentIncidentNote.Delete;
			break;
		case StudentNoteNoteType.Injury:
			noteTypePermissions = permissions.StudentInjuryNote.Delete;
			break;
		case StudentNoteNoteType.Medication:
			noteTypePermissions = permissions.StudentMedicationNote.Delete;
			break;
		case StudentNoteNoteType.SocialEmotional:
			noteTypePermissions = permissions.StudentSocialEmotionalNote.Delete;
			break;
	}

	return userPermissions[noteTypePermissions.Admin] || (isAuthor && userPermissions[noteTypePermissions.Basic]);
}
